import { useEffect, useMemo, useState } from 'react';
import { Label } from '@/components/ui/label';
import { chatbotAdminApi } from '@/features/chatbot/api/chatbotApi';
import type { TenantEmailAccountPublic } from '@workspace/types';

export function InstanceOtpEmailSection({
  tenantId,
  selectedAccountId,
  onSelectAccount,
}: {
  tenantId?: string;
  selectedAccountId: string | null;
  onSelectAccount: (accountId: string) => Promise<void>;
}) {
  const [accounts, setAccounts] = useState<TenantEmailAccountPublic[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return;
    }
    void (async () => {
      try {
        setLoading(true);
        const listed = await chatbotAdminApi.listTenantEmailAccounts(tenantId);
        setAccounts(listed);
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId]);

  const readyAccounts = useMemo(() => accounts.filter(account => account.ready), [accounts]);

  const resolved = useMemo(() => {
    if (selectedAccountId) {
      const picked = readyAccounts.find(account => account.id === selectedAccountId);
      if (picked) {
        return picked;
      }
    }
    if (readyAccounts.length === 1) {
      return readyAccounts[0];
    }
    return null;
  }, [readyAccounts, selectedAccountId]);

  if (loading) {
    return <p className='text-sm text-muted-foreground'>Cargando correo…</p>;
  }

  if (readyAccounts.length === 0) {
    return <p className='text-sm text-muted-foreground'>El correo todavía no está conectado.</p>;
  }

  if (resolved) {
    return <p className='text-sm text-muted-foreground'>Sale de {resolved.fromAddress}.</p>;
  }

  return (
    <div className='space-y-2'>
      <Label htmlFor='otp-email-account'>Correo para códigos</Label>
      <select
        id='otp-email-account'
        className='w-full border border-input bg-background text-foreground rounded-md p-2'
        value={selectedAccountId ?? ''}
        onChange={event => {
          const value = event.target.value;
          if (value) {
            void onSelectAccount(value);
          }
        }}
      >
        <option value=''>Elegí una dirección</option>
        {readyAccounts.map(account => (
          <option key={account.id} value={account.id}>
            {account.fromAddress}
          </option>
        ))}
      </select>
    </div>
  );
}
